// src/app/[lang]/components/ArticleSidebar.tsx
'use client';
import React, { useEffect, useMemo, useState } from 'react';
import { usePathname } from 'next/navigation';
import Link from 'next/link';
import LazyBookImage from './LazyBookImage';
import {
    GetJournalIssuesQuery,
    GetBooksQuery,
} from '@/gql/gql-generated';

interface ArticleSidebarProps {
    journalIssues: GetJournalIssuesQuery;
    books?: GetBooksQuery;
    lang: string;
    /** Mobile: render inside a closed <details> so the list does not push the article down */
    collapsible?: boolean;
}

// One shape for either parent, so the panel below is rendered once rather than twice.
interface SidebarPanel {
    heading: string;
    parentTitle?: string | null;
    parentHref: string;
    cover?: {
        src: string;
        alt: string;
        blurDataURL?: string;
    };
    items: { key: string; slug: string; label: string }[];
}

const ArticleSidebar = React.memo(({
    journalIssues,
    books,
    lang,
    collapsible = false,
}: ArticleSidebarProps) => {

    const pathname = usePathname();
    // Reading in book context: the chapter links carry ?context=book, and the article page uses the
    // same flag for its breadcrumb and prev/next. The sidebar has to follow it, otherwise you get the
    // journal's contents beside you while the navigation at the foot walks the book.
    //
    // Deliberately NOT useSearchParams(). This component is rendered from `article/layout.tsx`, which
    // is a `'use cache'` segment, so it is served from a static shell and useSearchParams() stays empty
    // even after hydration — verified in a real browser on 2026-09-12: location.search was
    // "?context=book" while the hook returned nothing. Reading the URL directly in an effect is the
    // only thing that reflects reality here. Costs one extra client render; the server shell shows the
    // journal panel first, which is the correct fallback anyway.
    const [context, setContext] = useState<string | null>(null);
    useEffect(() => {
        setContext(new URLSearchParams(window.location.search).get('context'));
    }, [pathname]);

    // Extract current article slug from URL
    const currentArticleSlug = useMemo(() => {
        const segments = pathname.split('/');
        const articleIndex = segments.findIndex(segment => segment === 'article');
        return articleIndex !== -1 ? segments[articleIndex + 1] : null;
    }, [pathname]);

    // Book panel — only when explicitly reading in book context.
    const bookPanel = useMemo<SidebarPanel | null>(() => {
        if (context !== 'book' || !currentArticleSlug) return null;
        const nodes = books?.books?.nodes ?? [];
        for (const book of nodes) {
            // Prefer the book's own `contents` list so the sidebar shows the same names the book's
            // table of contents does. Fall back to the flat relatedArticles list for unmigrated books.
            const contents = (book.bookDetails as any)?.contents ?? [];
            const chapters = contents.length
                ? contents
                      .map((row: any) => {
                          const a = row?.article?.nodes?.[0];
                          if (!a?.slug) return null;
                          return {
                              id: a.id,
                              slug: a.slug as string,
                              label:
                                  (row?.titleInBook || '').trim() ||
                                  (a.articleDetails?.tableOfContentsTitle || '').trim() ||
                                  a.title ||
                                  '',
                              before: (row?.labelBefore || '').trim(),
                          };
                      })
                      .filter(Boolean)
                : (book.bookDetails?.relatedArticles?.edges ?? [])
                      .map(edge => edge?.node)
                      // NOTE: filter on `slug`, not on __typename. getBooksAll.gql does NOT request
                      // __typename inside its `... on Article` selection (the journal query does), so
                      // n.__typename is undefined here and a __typename check silently matches nothing.
                      .filter(
                          (n): n is Extract<NonNullable<typeof n>, { slug?: string | null }> =>
                              !!n && 'slug' in n && !!n.slug
                      )
                      .map(n => ({
                          id: n.id,
                          slug: n.slug as string,
                          label:
                              (n.articleDetails?.tableOfContentsTitle || '').trim() || n.title || '',
                          before: '',
                      }));
            if (!chapters.some((c: any) => c.slug === currentArticleSlug)) continue;
            const img = book.featuredImage?.node;
            return {
                heading: 'Chapters in this Book',
                parentTitle: book.title,
                parentHref: `/${lang}/book/${book.slug}`,
                cover: img
                    ? {
                          src: img.mediaItemUrl || img.sourceUrl || '',
                          alt: img.altText || book.title || 'Book cover',
                          blurDataURL: img.thumbhash || undefined,
                      }
                    : undefined,
                items: chapters.map((c: any) => ({
                    key: `book-chapter-${c.id}`,
                    slug: c.slug,
                    label: c.before ? `${c.before} ${c.label}` : c.label,
                })),
            };
        }
        return null;
    }, [context, currentArticleSlug, books, lang]);

    // Journal panel — the default, unchanged behaviour.
    const journalPanel = useMemo<SidebarPanel | null>(() => {
        if (!currentArticleSlug || !journalIssues?.journalIssues?.nodes) return null;
        for (const journal of journalIssues.journalIssues.nodes) {
            const articles = journal.journalIssueDetails?.articlesInJournal?.nodes || [];
            const found = articles.find(
                article => article.__typename === 'Article' && article.slug === currentArticleSlug
            );
            if (!found) continue;
            const img = journal.featuredImage?.node;
            return {
                heading: 'Articles in this Issue',
                parentTitle: journal.title,
                parentHref: `/${lang}/journal/${journal.slug}`,
                cover: img
                    ? {
                          src: img.mediaItemUrl || img.sourceUrl || '',
                          alt: img.altText || journal.title || 'Journal cover',
                          blurDataURL: img.thumbhash || undefined,
                      }
                    : undefined,
                items: articles
                    .filter(
                        (a): a is Extract<NonNullable<typeof a>, { __typename?: 'Article' }> =>
                            !!a && a.__typename === 'Article'
                    )
                    .map(a => ({
                        key: `journal-article-${a.id}`,
                        slug: a.slug ?? '',
                        label:
                            a.articleDetails?.tableOfContentsTitle?.trim() || a.title || '',
                    })),
            };
        }
        return null;
    }, [currentArticleSlug, journalIssues, lang]);

    // Book context wins where it applies; fall back to the journal so an article that is in a book
    // but reached without the flag still gets its issue contents.
    const panel = bookPanel ?? journalPanel;

    if (!panel) {
        return null;
    }

    const suffix = panel === bookPanel ? '?context=book' : '';

    const body = (
        <div>
            {panel.cover && (
                <Link href={panel.parentHref}>
                    <div className="relative aspect-[2/3] mt-3" style={{ maxWidth: collapsible ? '96px' : '40%' }}>
                        <LazyBookImage
                            src={panel.cover.src}
                            alt={panel.cover.alt}
                            blurDataURL={panel.cover.blurDataURL}
                            priority={true}
                            quality={75}
                            sizes="150px"
                            className="rounded-lg shadow-md"
                        />
                    </div>
                </Link>
            )}

            {panel.parentTitle && (
                <Link href={panel.parentHref}>
                    <span className="block mt-0 text-base font-semibold hyperlink">
                        {panel.parentTitle}
                    </span>
                </Link>
            )}

            {panel.items.length > 0 && (
                <div className="mt-2">
                    {/* In the collapsible variant the <summary> is the heading */}
                    {!collapsible && <h2 className="mb-2 mt-0 text-lg">{panel.heading}</h2>}
                    <ul className="list-none m-0 p-0">
                        {panel.items.map((item, index) => {
                            const isCurrent = item.slug === currentArticleSlug;
                            return (
                                <li key={item.key} className="mb-4 flex items-start">
                                    <div className="flex flex-col items-center">
                                        <div
                                            className={`w-4 h-4 rounded-full mt-1 ${
                                                isCurrent ? 'bg-communist-red' : 'bg-gray-300'
                                            }`}
                                        ></div>
                                        {index !== panel.items.length - 1 && (
                                            <div className="flex-1 w-px bg-gray-300"></div>
                                        )}
                                    </div>
                                    <div className="ml-4">
                                        {isCurrent ? (
                                            // Bold + red + the filled marker: not colour alone (WCAG 1.4.1)
                                            <span className="font-bold text-communist-red" aria-current="page">
                                                {item.label}
                                            </span>
                                        ) : (
                                            <Link
                                                href={`/${lang}/article/${item.slug}${suffix}`}
                                                className="hover:text-communist-red hover:underline"
                                            >
                                                {item.label}
                                            </Link>
                                        )}
                                    </div>
                                </li>
                            );
                        })}
                    </ul>
                </div>
            )}
        </div>
    );

    if (!collapsible) return body;

    // Native <details>: keyboard-operable and announced as expandable with no JS.
    return (
        <details className="border-y border-gray-300 py-3">
            <summary className="cursor-pointer font-cambay font-bold text-lg text-communist-red marker:text-communist-red">
                {panel.heading}
                <span className="ml-2 font-normal text-sm text-gray-600">({panel.items.length})</span>
            </summary>
            <div className="mt-3">{body}</div>
        </details>
    );
});

ArticleSidebar.displayName = 'ArticleSidebar';
export default ArticleSidebar;
